/**
 * Auth Actions - Shared helpers (Hono-era)
 */

import type { Context } from 'hono'
import type { ModuleContext } from '@gzl10/nexus-sdk'
import type { AuthConfig, RequestInfo } from '../auth.types.js'
import type { CookieOptions } from 'hono/utils/cookie'

/** Extract client IP from x-forwarded-for / x-real-ip headers (no Node conninfo dep). */
export function getClientIp(c: Context | undefined): string | undefined {
  if (!c) return undefined
  const fwd = c.req.header('x-forwarded-for')
  if (fwd) return fwd.split(',')[0]?.trim()
  return c.req.header('x-real-ip') ?? undefined
}

/** Build cookie options for the refresh token cookie. */
export function getCookieOptions(c: Context | undefined, cookieDomain?: string): CookieOptions {
  const proto = c?.req.header('x-forwarded-proto')
  const isSecure = proto === 'https'
  return {
    httpOnly: true,
    secure: isSecure,
    sameSite: isSecure ? 'Strict' : 'Lax',
    path: '/api/v1',
    maxAge: 7 * 24 * 60 * 60, // 7 days, Hono uses seconds
    ...(cookieDomain ? { domain: cookieDomain } : {})
  }
}

/** Extract request info for audit from Hono Context. */
export function getRequestInfo(c: Context | undefined, deviceInfo?: { deviceId?: string; deviceName?: string }): RequestInfo {
  return {
    ip: getClientIp(c),
    userAgent: c?.req.header('user-agent'),
    deviceId: deviceInfo?.deviceId,
    deviceName: deviceInfo?.deviceName
  }
}

/**
 * Create rate-limit middleware instances for auth flows.
 * NOTE: IP-only keys here (request body is not available before Hono routes read JSON).
 */
export function createRateLimits(ctx: ModuleContext, authConfig: AuthConfig) {
  const { rateLimit } = ctx.core.middleware

  return {
    login: rateLimit({
      windowMs: authConfig.rateLimitWindowMs,
      max: authConfig.rateLimitMax,
      message: 'Too many login attempts, please try again later',
      skipSuccessfulRequests: true
    }),
    refresh: rateLimit({
      windowMs: 60 * 1000,
      max: authConfig.rateLimitMax * 2,
      message: 'Too many refresh requests'
    }),
    logout: rateLimit({
      windowMs: 60 * 1000,
      max: authConfig.rateLimitMax,
      message: 'Too many logout requests'
    }),
    admin: rateLimit({
      windowMs: 60 * 1000,
      max: 10,
      message: 'Too many admin requests, please try again later'
    })
  }
}
